import { useState } from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Globe, Users, BookOpen } from 'lucide-react';
import Section from '../components/Section';
import Button from '../components/Button';
import VideoModal from '../components/VideoModal';
import PageHero from '../components/PageHero';
import { SF_UI } from '../components/ui/tokens';

const EducationPage = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isSecondVideoModalOpen, setIsSecondVideoModalOpen] = useState(false);

  return (
    <>
      <PageHero
        eyebrow="Education"
        title="Haneul Institute"
        subtitle="The First Decentralized University on Apertum Blockchain — a new horizon for those who dream beyond borders."
        icon={<GraduationCap size={28} className="text-primary-100" />}
      />
      <Section bgColor={SF_UI.sectionBand} className="pt-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className={`${SF_UI.surfaceCard} ${SF_UI.surfaceCardPad}`}
            >
              <p className="text-lg text-primary italic mb-6">
                &quot;A new horizon is rising for those who dream beyond borders&quot;
              </p>
              <p className="text-gray-700 mb-6">
                We are pleased to formally announce the establishment of the Haneul Institute, 
                the world's first decentralized university (DeUni) designed specifically for 
                athletes, builders, and free thinkers. This groundbreaking educational initiative 
                represents a fundamental shift in how higher learning is structured, delivered, 
                and recognized.
              </p>
              <div className="flex gap-4 flex-wrap">
                <Button variant="primary" size="lg" to="/faq">
                  Learn More
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  as="a"
                  href="https://www.youtube.com/playlist?list=PLHlf_ssJTHkfiTCBlU_8E7rq4sErqRNss"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Watch Lessons
                </Button>
                <Button
                  size="lg"
                  as="a"
                  href="https://www.youtube.com/playlist?list=PLHlf_ssJTHkefIUUyrxcMyDn6ta7bfn4n"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-yellow-500 hover:bg-yellow-600 text-white shadow-sm hover:shadow"
                >
                  NFT MasterClass
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative p-2 bg-gradient-to-br from-primary-200 to-primary-400 rounded-lg shadow-[0_15px_30px_-8px_rgba(0,0,0,0.4),0_10px_20px_-10px_rgba(0,0,0,0.3)] max-w-md w-full transform hover:-translate-y-2 hover:shadow-[0_20px_35px_-12px_rgba(0,0,0,0.5),0_15px_25px_-15px_rgba(0,0,0,0.4)] transition-all duration-300">
                <div className="aspect-video rounded overflow-hidden">
                  <iframe
                    src="https://www.youtube.com/embed/7kj0e4D93XY"
                    title="Haneul Institute Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="7kj0e4D93XY"
      />

      <Section>
        <div className="max-w-2xl mx-auto">
          <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex justify-center"
            >
              <div className="relative p-2 bg-gradient-to-br from-primary-200 to-primary-400 rounded-lg shadow-[0_15px_30px_-8px_rgba(0,0,0,0.4),0_10px_20px_-10px_rgba(0,0,0,0.3)] max-w-md w-full transform hover:-translate-y-2 hover:shadow-[0_20px_35px_-12px_rgba(0,0,0,0.5),0_15px_25px_-15px_rgba(0,0,0,0.4)] transition-all duration-300">
                <div className="aspect-video rounded overflow-hidden">
                  <iframe
                    src="https://www.youtube.com/embed/rKOu54QgE2g?list=PLHlf_ssJTHkcDSduPJ07kM0izAfrmX-wK"
                    title="SportsFuture Education Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            </motion.div>
        </div>
      </Section>

      <Section bgColor="bg-primary-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Our Foundation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 md:justify-center gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary">
                  <BookOpen size={32} />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-center mb-3">
                SportsFuture Technology
              </h3>
              <p className="text-gray-600 text-center">
                Powered by cutting-edge SportsFuture technology infrastructure
              </p>
            </motion.div>

            <a href="https://apertum.io" target="_blank" rel="noopener noreferrer" className="block group">
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: 0.4 }}
    className="bg-white p-6 rounded-2xl relative sf-surface-card sf-ui-system overflow-hidden
      /* Base shadow and effects for all devices */
      shadow-md transition-all duration-300
      /* Mobile-specific always-on effects */
      sm:border-primary sm:border
      /* Desktop hover effects (preserved) */
      hover:shadow-xl hover:-translate-y-1 hover:bg-primary-50 cursor-pointer"
  >
    {/* Flash effect overlay (hover on desktop only) */}
    <div className="absolute inset-0 bg-primary opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
    
    {/* Border effect - visible on mobile, appears on hover for desktop */}
    <div className="absolute inset-0 border-2 border-primary sm:opacity-30 md:opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-300"></div>
    
    <div className="flex justify-center mb-4">
      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary
        /* Subtle animation only on mobile */
        sm:animate-[subtlePulse_2s_infinite]
        /* Effects on desktop hover */
        group-hover:bg-primary-200 group-hover:scale-110 transition-all duration-300">
        <Users size={32} />
      </div>
    </div>
    
    <h3 className="text-xl font-semibold text-center mb-3 group-hover:text-primary transition-colors duration-300">
      Apertum Blockchain
    </h3>
    
    <p className="text-gray-600 text-center mb-2 group-hover:text-gray-700 transition-colors duration-300">
      Built on the innovative Apertum Blockchain platform
    </p>
    
    {/* Learn more text - always visible on mobile, appears on hover for desktop */}
    <div className="flex justify-center items-center mt-4 sm:opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <span className="text-primary font-medium">Learn More</span>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </div>
  </motion.div>
</a>
          </div>
        </div>
      </Section>

      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Our Vision</h2>
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <h3 className="text-xl font-semibold mb-3">Truly Borderless Education</h3>
              <p className="text-gray-600">
                Eliminating traditional boundaries through a system that is truly borderless 
                in its reach and accessibility.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <h3 className="text-xl font-semibold mb-3">Merit-Based Recognition</h3>
              <p className="text-gray-600">
                Credentials earned through demonstrable contributions and achievements rather 
                than traditional academic metrics.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white p-6 rounded-2xl shadow-md sf-surface-card sf-ui-system"
            >
              <h3 className="text-xl font-semibold mb-3">Community-Driven Evolution</h3>
              <p className="text-gray-600">
                An educational ecosystem that grows organically, shaped by its community of 
                learners and contributors.
              </p>
            </motion.div>
          </div>
        </div>
      </Section>

      <Section bgColor="bg-primary-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Launch Strategy</h2>
          <p className="text-lg text-gray-700 mb-8">
            What begins as a controlled, limited release will systematically evolve into a 
            comprehensive educational ecosystem. The institute will grow organically, shaped 
            by its community of learners and contributors, all united by a commitment to 
            decentralized education.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-md sf-surface-card sf-ui-system">
            <h3 className="text-xl font-semibold mb-4">Engagement Opportunity</h3>
            <p className="text-gray-600 mb-6">
              We invite interested parties to follow the development of the Haneul Institute 
              through our official channels. Updates on curriculum development, partnership 
              opportunities, and registration information will be provided in the coming weeks.
            </p>
            <Button variant="primary" to="/contact">
              Contact Executive Office
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};

export default EducationPage;
